/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import gsap from "gsap";
import "gsap/EasePack";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import ScrollSmoother from "gsap/ScrollSmoother";
gsap.registerPlugin(ScrollSmoother, ScrollTrigger, SplitText);

import Swiper from "swiper";
import {
	Autoplay,
	FreeMode,
	Thumbs,
	EffectFade,
	Navigation,
	Pagination,
	Scrollbar,
} from "swiper/modules";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

let scrollSmooth = false;
const hzBreakpointWidth = 1440;
const hzBreakpointHeight = 680;

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'no-js' body class
	document.body.classList.remove("no-js");
	gsapPreloader();
	scrollDetection(50);
	menuBurger();
	menuProduct();
	menuDropdown();
	initBanner();
	bannerToMarquee();
	popinInit();
	toggleInit();
	accordionInit();
	srcInit();
	brandsFilterInit();
});

// DOCUMENT RESIZED (avec ScrollTrigger)
ScrollTrigger.addEventListener("refreshInit", () => {
	gsapScrollSmootherRefresh();
	bannerToMarquee();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Activation/désactivation du scroll
function scrollEnabled(bool) {
	if (bool === false || bool === true) {
		const enableScroll = bool === true;

		if (scrollSmooth) {
			scrollSmooth.paused(!enableScroll);
		}

		document.documentElement.style.overflowY = enableScroll ? "auto" : "hidden";
	}
}

// Scroll vers une ancre
const scrollToAnchor = (scrollOffset = 100) => {
	// Fonction pour gérer le défilement
	const scrollToElement = (target) => {
		const element = document.querySelector(target);
		if (element) {
			if (scrollSmooth) {
				// Avec ScrollSmoother
				scrollSmooth.scrollTo(target, true, `top ${scrollOffset}px`);
			} else {
				// Par défaut
				window.scrollTo({
					top:
						element.getBoundingClientRect().top + window.scrollY - scrollOffset,
					behavior: "smooth",
				});
			}
		}
	};

	// au chargement
	const hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollToElement(hashInUrl);
	}

	// avec un [href="#..."]
	const linksWithAnchor = document.querySelectorAll(
		'a[href^="#"]:not([disabled])'
	);
	linksWithAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			const anchorHref = link.getAttribute("href");
			if (anchorHref !== "#") {
				event.preventDefault();
				scrollToElement(anchorHref);
			}
		});
	});

	// avec un [data-scrollto]
	const dataWithScrollTo = document.querySelectorAll(
		"[data-scrollto]:not([disabled])"
	);
	dataWithScrollTo.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.getAttribute("data-scrollto");
			if (anchorData === "auto") {
				// Calculer la position de défilement pour 'auto'
				const autoOffset = window.innerHeight + window.scrollY - scrollOffset;
				window.scrollTo({
					top: autoOffset,
					behavior: "smooth",
				});
			} else {
				// Utiliser la valeur de data-scrollto comme cible pour scrollToElement
				scrollToElement(anchorData);
			}
		});
	});
};

// Détection du scroll sur le body
const scrollDetection = (scrollOffset = 1) => {
	let position = window.scrollY;

	if (position > scrollOffset) {
		document.body.classList.add("page-with-scrolled");
	} else {
		document.body.classList.remove("page-with-scrolled");
	}

	document.addEventListener("scroll", () => {
		// Détecter la direction du scroll
		let scroll = window.scrollY;
		// Scroll vers le bas
		if (scroll > position) {
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-with-scrolled");
				document.body.classList.remove("page-with-scrolling");
			} else {
				document.body.classList.add("page-with-scrolling");
				document.body.classList.add("page-with-scrolled");
			}
		}
		// Scroll vers le haut
		else {
			document.body.classList.remove("page-with-scrolling");
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-with-scrolled");
			}
		}
		// Scroll max ?
		if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
			document.body.classList.add("page-scroll-completed");
		} else {
			document.body.classList.remove("page-scroll-completed");
		}
		position = scroll;
	});
};

// Gestion du menu burger
const menuBurger = () => {
	const headerBurgerButton = document.querySelector("#page-header-burger");
	const headerBurgerArea = document.querySelector("#page-header-fullmenu");

	if (headerBurgerButton) {
		headerBurgerButton.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullmenu");
			}
		});

		document.addEventListener("mouseup", (event) => {
			if (
				document.body.classList.contains("page-with-fullmenu") &&
				!headerBurgerButton.contains(event.target) &&
				headerBurgerButton !== event.target &&
				!headerBurgerArea.contains(event.target) &&
				headerBurgerArea !== event.target
			) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullmenu");
			}
		});
	}
};

// Gestion du menu de produits
const menuProduct = () => {
	const headerProductButton = document.querySelector("#page-header-product");
	const headerProductArea = document.querySelector("#page-header-popin");

	if (headerProductButton) {
		headerProductButton.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-popin")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-popin");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-popin");
			}
		});

		document.addEventListener("mouseup", (event) => {
			if (
				document.body.classList.contains("page-with-popin") &&
				!headerProductButton.contains(event.target) &&
				headerProductButton !== event.target &&
				!headerProductArea.contains(event.target) &&
				headerProductArea !== event.target
			) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-popin");
			}
		});
	}
};

// Gestion des dropdowns
const menuDropdown = () => {
	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]'
	);

	menuItemLinks.forEach((item) => {
		item.closest(".menu__item").classList.add("menu__item--parent");
		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active"
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
			}
		});
	});
};

// Activation/désactivation de la bannière
const initBanner = () => {
	// Vérifie si la bannière a déjà été fermée durant cette session
	if (sessionStorage.getItem("bannerClosed") === "true") {
		return;
	}

	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// actualisation
		document.body.classList.add("page-with-banner");
		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");

			// Enregistre en session que la bannière a été fermée
			sessionStorage.setItem("bannerClosed", "true");
		});
	}
};

// Gestion du défilement horizontal de la bannière
const bannerToMarquee = () => {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;
		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("--scrolling");
		} else {
			pageBanner.classList.add("--scrolling");
		}
	}
};

// Gestions des actions des [data-toggle]
const toggleInit = () => {
	// Récupération de tous les écouteurs
	const toggleListeners = document.querySelectorAll("[data-toggle]");

	// Écouteur d'événement pour chaque écouteur
	toggleListeners.forEach((toggleListener) => {
		const toggleTargetSelector = toggleListener.dataset.toggle;

		// Ajout d'un événement "click" à chaque écouteur
		toggleListener.addEventListener("click", (event) => {
			// event.stopPropagation();

			// Désactiver tous les toggles et leurs cibles
			toggleListeners.forEach((listener) => {
				const targets = listener.dataset.toggle
					? document.querySelectorAll(listener.dataset.toggle)
					: null;

				if (targets) {
					listener.classList.remove("--active");
					targets.forEach((target) => target.classList.remove("--active"));
				}
			});

			// Récupérer les toggles ayant le même sélecteur de cible
			const similarToggleListeners = document.querySelectorAll(
				`[data-toggle="${toggleTargetSelector}"]`
			);
			const toggleTargets = toggleTargetSelector
				? document.querySelectorAll(toggleTargetSelector)
				: null;

			// Vérifier si les éléments sont actifs
			const isActive =
				toggleTargets &&
				Array.from(toggleTargets).every((target) =>
					target.classList.contains("--active")
				);

			// Si le toggle cliqué n'était pas actif, l'activer avec ses cibles
			if (!isActive && toggleTargets) {
				similarToggleListeners.forEach((listener) => {
					listener.classList.add("--active");
				});

				// Activer toutes les cibles associées
				toggleTargets.forEach((target) => target.classList.add("--active"));
			}
		});
	});
};

// Gestions des actions des [data-accordion]
const accordionInit = () => {
	const accordionListeners = document.querySelectorAll("[data-accordion]");

	accordionListeners.forEach((accordionElement) => {
		accordionElement.addEventListener("click", (event) => {
			// event.stopPropagation();

			// Récupérer l'élément cible de l'accordéon actuel
			const currentAccordionTarget = document.querySelector(
				`#${accordionElement.dataset.accordion}`
			);

			// Vérifier si l'accordéon cliqué est déjà actif
			const isActive = accordionElement.classList.contains("--active");

			// On change l'état de l'accordéon
			if (!isActive && currentAccordionTarget) {
				accordionElement.classList.add("--active");
				currentAccordionTarget.classList.add("--active");
			} else {
				accordionElement.classList.remove("--active");
				currentAccordionTarget.classList.remove("--active");
			}
		});
	});
};

// Gestions des actions des [data-popin]
const popinInit = () => {
	const popins = document.querySelectorAll("[data-popin]");

	popins.forEach((popin) => {
		popin.addEventListener("click", (event) => {
			event.preventDefault();

			// ouvrir/fermer la popin
			const thispopinName = popin.dataset.popin;
			if (thispopinName) {
				const thispopinEl = document.querySelector(`#${thispopinName}`);
				thispopinEl.classList.toggle("--open");

				// Vérifier si la classe --open est présente pour bloquer ou débloquer le scroll
				if (thispopinEl.classList.contains("--open")) {
					scrollEnabled(false);
				} else {
					scrollEnabled(true);
				}
			}
		});
	});
};

// Conversion des [data-src] en [src]
const srcInit = () => {
	// Select all elements with a data-src attribute
	const srcElements = document.querySelectorAll("[data-src]");
	srcElements.forEach((element) => {
		element.setAttribute("src", element.getAttribute("data-src"));
		element.removeAttribute("data-src");
	});
};

// Gestion des filtres de la page #marques
const brandsFilterInit = () => {
	const pageMarque = document.querySelector("#marques");
	if (!pageMarque) return;

	const letters = document.querySelectorAll("[data-letter]");
	const letterTargets = document.querySelectorAll("[data-letter-target]");
	const categories = document.querySelectorAll("[data-category]");
	const categoryTargets = document.querySelectorAll("[data-category-target]");
	const reset = document.querySelector("[data-reset]");

	// Refraichis les filtres
	function resetFilters(exception) {
		switch (exception) {
			case "categories":
				// Gérer les data-category
				categories.forEach((category) => {
					category.classList.remove("--active");
				});
				break;
			case "letters":
				letters.forEach((letter) => {
					letter.classList.remove("--active");
				});
				break;
			default:
				// Gérer les data-category
				categories.forEach((category) => {
					category.classList.remove("--active");
				});
				// Gérer les data-letter
				letters.forEach((letter) => {
					letter.classList.remove("--active");
				});
				// Gérer les letterTargets individuels (slides)
				letterTargets.forEach((target) => {
					target.classList.remove("--hidden");
				});
				// MAJ sur les [data-category-target]
				checkCategories(categoryTargets);
				// Gérer le button reset
				reset.classList.add("--hidden");
		}
	}

	// Refraichis les [data-letter-target]
	function checkCategories(categoryTargets) {
		categoryTargets.forEach((categoryTarget) => {
			const letterTargets = categoryTarget.querySelectorAll(
				"[data-letter-target]"
			);
			// Vérifie si tous les éléments [data-letter-target] de categoryTarget contiennent la classe "--hidden"
			const allHidden = Array.from(letterTargets).every((target) =>
				target.classList.contains("--hidden")
			);
			if (allHidden) {
				categoryTarget.classList.add("--hidden");
			} else {
				categoryTarget.classList.remove("--hidden");
			}
		});
	}

	// Ajoute la classe "button-disabled" aux lettres sans cible correspondante
	const availableLetter = new Set(
		// Crée un Set pour les lettres qui ont des cibles correspondantes
		[...letterTargets].map((target) => target.dataset.letterTarget)
	);
	letters.forEach((letter) => {
		if (!availableLetter.has(letter.dataset.letter)) {
			letter.classList.add("button-disabled");
			letter.setAttribute("disabled", true);
		}
	});

	// Actions pour chaque [data-category]
	categories.forEach((category) => {
		// Gestion du clic sur les éléments data-category
		category.addEventListener("click", () => {
			const clickedcategory = category.dataset.category;

			// Si le bouton a la classe "button-disabled" ou l'attribut "disabled" ou aucune valeur pour data.category, ne rien faire
			if (
				category.classList.contains("button-disabled") ||
				category.hasAttribute("disabled") ||
				clickedcategory == ""
			) {
				return;
			}

			// Gérer les [data-category-target]
			categoryTargets.forEach((target) => {
				const isMatch = target.dataset.categoryTarget === clickedcategory;
				if (isMatch) {
					target
						.querySelectorAll("[data-letter-target]")
						.forEach((letterTarget) => {
							letterTarget.classList.remove("--hidden");
						});
				} else {
					target
						.querySelectorAll("[data-letter-target]")
						.forEach((letterTarget) => {
							letterTarget.classList.add("--hidden");
						});
				}
			});

			// Gérer les [data-category]
			categories.forEach((l) => l.classList.remove("--active"));
			category.classList.add("--active");

			// MAJ sur les [data-category-target]
			checkCategories(categoryTargets);

			// Activer le bouton reset
			reset.classList.remove("--hidden");

			// Réinitialisér les lettres disponibles
			resetFilters("letters");
		});
	});

	// Actions pour chaque [data-letter]
	letters.forEach((letter) => {
		// Gestion du clic sur les éléments data-letter
		letter.addEventListener("click", () => {
			const clickedLetter = letter.dataset.letter;

			// Si le bouton a la classe "button-disabled" ou l'attribut "disabled" ou aucune valeur pour data.letter, ne rien faire
			if (
				letter.classList.contains("button-disabled") ||
				letter.hasAttribute("disabled") ||
				clickedLetter == ""
			) {
				return;
			}

			// Gérer les letterTargets individuels (slides)
			letterTargets.forEach((target) => {
				const isMatch = target.dataset.letterTarget === clickedLetter;
				if (isMatch) {
					target.classList.remove("--hidden");
				} else {
					target.classList.add("--hidden");
				}
			});

			// Gérer les data-letter
			letters.forEach((l) => l.classList.remove("--active"));
			letter.classList.add("--active");

			// MAJ sur les [data-category-target]
			checkCategories(categoryTargets);

			// Activer le bouton reset
			reset.classList.remove("--hidden");

			// Réinitialisér les lettres disponibles
			resetFilters("categories");
		});
	});

	// Gestion du clic sur le bouton letterResets
	reset.addEventListener("click", resetFilters);
};

/* -------------------------------- (SWIPER) -------------------------------- */

// Swiper : Initialisation des sliders
const swiperInit = () => {
	// .swiper[data-swiper='scrollbar']
	if (
		document.querySelectorAll(".swiper[data-swiper='scrollbar'] .swiper-slide")
			.length > 1
	) {
		new Swiper(".swiper[data-swiper='scrollbar']", {
			// Core parameters
			slidesPerView: "auto",
			speed: 1200,
			resizeObserver: true,
			// Modules
			modules: [Scrollbar],
			scrollbar: {
				el: ".swiper[data-swiper='scrollbar'] .swiper-scrollbar",
				draggable: true,
				hide: true,
			},
		});
	}

	// .swiper[data-swiper='navigation']
	if (
		document.querySelectorAll(".swiper[data-swiper='navigation'] .swiper-slide")
			.length > 1
	) {
		new Swiper(".swiper[data-swiper='navigation']", {
			// Core parameters
			slidesPerView: "auto",
			speed: 1200,
			resizeObserver: true,
			// Modules
			modules: [Navigation],
			navigation: {
				nextEl: ".swiper-button-next",
				prevEl: ".swiper-button-prev",
			},
		});
	}

	// .swiper[data-swiper='menu-xxx']
	if (
		document.querySelectorAll(
			".swiper[data-swiper='menu-thumbs'] .swiper-slide"
		).length > 1 &&
		document.querySelectorAll(".swiper[data-swiper='menu-links'] .swiper-slide")
			.length > 1
	) {
		const swiperMenuThumbs = new Swiper(".swiper[data-swiper='menu-thumbs']", {
			// Core parameters
			slidesPerView: "auto",
			speed: 600,
			resizeObserver: true,
			watchSlidesProgress: true,
			// Modules
			modules: [FreeMode, Navigation],
			freeMode: {
				enabled: true,
			},
			navigation: {
				nextEl: ".swiper-button-next",
				prevEl: ".swiper-button-prev",
			},
		});
		const swiperMenuLinks = new Swiper(".swiper[data-swiper='menu-links']", {
			// Core parameters
			slidesPerView: "auto",
			speed: 300,
			resizeObserver: true,
			// Modules
			modules: [EffectFade, Thumbs],
			effect: "fade",
			thumbs: {
				swiper: swiperMenuThumbs,
			},
		});
	}

	// .swiper[data-swiper='brands']
	if (
		document.querySelectorAll(".swiper[data-swiper='brands'] .swiper-slide")
			.length > 1
	) {
		new Swiper(".swiper[data-swiper='brands']", {
			// Core parameters
			slidesPerView: "auto",
			speed: 1200,
			resizeObserver: true,
			// Modules
			modules: [FreeMode, Pagination],
			freeMode: {
				enabled: true,
			},
			pagination: {
				el: ".swiper[data-swiper='brands'] .swiper-pagination",
				clickable: true,
			},
		});
	}

	// .swiper[data-swiper='featured-brands']
	if (
		document.querySelectorAll(
			".swiper[data-swiper='featured-brands'] .swiper-slide"
		).length > 1
	) {
		const slideCount = document.querySelectorAll(
			".swiper[data-swiper='featured-brands'] .swiper-slide"
		).length;
		new Swiper(".swiper[data-swiper='featured-brands']", {
			// Core parameters
			slidesPerView: "auto",
			speed: slideCount * 100,
			resizeObserver: true,
			loop: true,
			// Modules
			modules: [Autoplay, FreeMode],
			autoplay: {
				delay: 0,
			},
			freeMode: {
				enabled: true,
			},
		});
	}

	// .swiper[data-swiper='partners']
	if (
		document.querySelectorAll(".swiper[data-swiper='partners'] .swiper-slide")
			.length > 1
	) {
		new Swiper(".swiper[data-swiper='partners']", {
			// Core parameters
			slidesPerView: "auto",
			speed: 1800,
			resizeObserver: true,
			loop: true,
			direction: "horizontal",
			breakpoints: {
				[hzBreakpointWidth]: {
					direction: "vertical",
				},
			},
			// Modules
			modules: [Autoplay],
			autoplay: {
				delay: 1800,
			},
		});
	}

	// .swiper[data-swiper='category']
	if (
		document.querySelectorAll(".swiper[data-swiper='category'] .swiper-slide")
			.length > 1
	) {
		new Swiper(".swiper[data-swiper='category']", {
			// Core parameters
			slidesPerView: "auto",
			speed: 1200,
			resizeObserver: true,
			breakpoints: {
				1440: {
					enabled: false,
				},
			},
			// Modules
			modules: [FreeMode, Scrollbar],
			freeMode: {
				enabled: true,
			},
			scrollbar: {
				el: ".swiper[data-swiper='category'] .swiper-scrollbar",
				draggable: true,
				hide: true,
			},
		});
	}

	// .swiper[data-swiper='product-xxx']
	if (
		document.querySelectorAll(
			".swiper[data-swiper='product-thumbs'] .swiper-slide"
		).length > 1 &&
		document.querySelectorAll(
			".swiper[data-swiper='product-medias'] .swiper-slide"
		).length > 1
	) {
		const swiperProductThumbs = new Swiper(
			".swiper[data-swiper='product-thumbs']",
			{
				// Core parameters
				slidesPerView: "auto",
				speed: 600,
				resizeObserver: true,
				watchSlidesProgress: true,
				// Modules
				modules: [FreeMode],
				freeMode: {
					enabled: true,
				},
			}
		);
		const swiperProductMedias = new Swiper(
			".swiper[data-swiper='product-medias']",
			{
				// Core parameters
				slidesPerView: "auto",
				speed: 600,
				resizeObserver: true,
				// Modules
				modules: [Thumbs],
				thumbs: {
					swiper: swiperProductThumbs,
				},
			}
		);
	}
};

/* --------------------------------- (GSAP) --------------------------------- */

// GSAP : Initialisation du scrollSmoother (avec ScrollSmoother)
const initScrollSmooth = () => {
	scrollSmooth = ScrollSmoother.create({
		wrapper: "#page-wrapper",
		content: "#page-container",
		smooth: 1,
		smoothTouch: false,
		effects: true,
		normalizeScroll: false,
		ignoreMobileResize: true,
	});
};

// GSAP : Initialisation du scroll horizontal
const initHorizontalScroll = (
	bpWidth = hzBreakpointWidth,
	bpHeight = hzBreakpointHeight
) => {
	if (document.body.classList.contains("template-home")) {
		let mm = gsap.matchMedia();

		// Format vertical classique (mobile)
		mm.add(
			`(max-width: ${bpWidth - 0.02}px) or (max-height: ${bpHeight - 0.02}px)`,
			() => {
				// Scroll classique
				document.body.classList.remove("page-with-horizontal-scroll");
				gsapAnimations();
			}
		);

		// Format horizontal (desktop)
		mm.add(`(min-width: ${bpWidth}px) and (min-height: ${bpHeight}px)`, () => {
			// Scroll horizontal
			document.body.classList.add("page-with-horizontal-scroll");
			let horizontalWrapper = document.querySelector("#page-article");
			let horizontalScroll = gsap.to(horizontalWrapper, {
				x: () =>
					-1 *
					(horizontalWrapper.scrollWidth -
						document.documentElement.clientWidth) +
					"px",
				ease: "none",
			});
			ScrollTrigger.create({
				trigger: horizontalWrapper,
				start: "top top",
				end: () => "+=" + horizontalWrapper.offsetWidth,
				scrub: 1,
				pin: true,
				pinSpacing: true,
				invalidateOnRefresh: true,
				animation: horizontalScroll,
				// onUpdate: function () {
				// 	const totalHzScroll = document.body.scrollHeight - window.innerHeight;
				// 	let currentHzScroll = window.scrollY;
				// 	console.log(`${Math.round((currentHzScroll * 100) / totalHzScroll)}%`);
				// },
				// markers: true,
			});
			gsapHorizontalAnimations(horizontalScroll);
		});
	}
};

// GSAP : Découpage des textes (avec SplitText)
const gsapSplitText = () => {
	const textsToAnimated = document.querySelectorAll([
		// global
		'[data-animation^="reveal-text"]',
		".elementor-heading-title",
	]);

	textsToAnimated.forEach((_thisText) => {
		new SplitText(_thisText, {
			tag: "span",
			// type: "lines, words, chars",
			type: "lines, words",
			linesClass: "line",
			wordsClass: "word",
			charsClass: "char",
		});
	});
};

// GSAP : Animations au scroll (avec ScrollTrigger)
const gsapAnimations = () => {
	document.body.classList.add("page-with-animations");
	if (!document.body.classList.contains("page-with-horizontal-scroll")) {
		// [data-animation] + .elementor-element
		const elementsToAnimated = document.querySelectorAll([
			"[data-animation]",
			".elementor-heading-title",
			".elementor-element > .elementor-element > .elementor-element:not([data-animation])",
		]);
		elementsToAnimated.forEach((_this) => {
			const delay = _this.getAttribute("data-animation-delay")
				? parseInt(_this.getAttribute("data-animation-delay"), 10)
				: 0;
			ScrollTrigger.create({
				trigger: _this,
				start: "top 100%",
				end: "bottom 0",
				onEnter: () => {
					setTimeout(() => {
						_this.classList.add("--animated");
					}, delay);
				},
				onLeaveBack: () => {
					_this.classList.remove("--animated");
				},
				// markers: true,
			});
		});

		// data-animation="counter"
		const elementsToCounter = document.querySelectorAll(
			'[data-animation="counter"]'
		);
		elementsToCounter.forEach((_this) => {
			const delay = _this.getAttribute("data-animation-delay")
				? parseInt(_this.getAttribute("data-animation-delay"), 10)
				: 0;
			ScrollTrigger.create({
				trigger: _this,
				start: "top 100%",
				end: "bottom 0%",
				// markers: true,
				onEnter: () => {
					setTimeout(() => {
						gsap.from(_this, {
							duration: 1.5,
							textContent: 0,
							snap: { textContent: 1 },
							stagger: {
								each: 1.0,
								onUpdate: function () {
									this.targets()[0].innerHTML = Math.ceil(
										this.targets()[0].textContent
									)
										// conserver les "."
										.toString()
										.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
										// conserver les espaces
										.toString()
										.replace(/\B(?=(\d{3})+(?!\d))/g, "&nbsp;");
								},
							},
						});
					}, delay);
				},
			});
		});

		// data-animation="reveal-text-reading"
		const textsToReading = document.querySelectorAll([
			'[data-animation="reveal-text-reading"]',
		]);
		textsToReading.forEach((_this) => {
			const delay = _this.getAttribute("data-animation-delay")
				? parseInt(_this.getAttribute("data-animation-delay"), 10)
				: 0;
			gsap.fromTo(
				_this.querySelectorAll(".word"),
				{
					opacity: 0.33,
				},
				{
					delay: delay,
					stagger: 0.3,
					ease: "power1.out",
					opacity: 1,
					scrollTrigger: {
						trigger: _this,
						start: "top 90%",
						end: "top 20%",
						scrub: true,
						// markers: true,
					},
				}
			);
		});

		// #cta-home
		const ctaHome = document.querySelector("#cta-home");
		if (ctaHome) {
			ScrollTrigger.create({
				trigger: document.querySelector(".archive-featured-product-category"),
				start: "left +=50%",
				end: "right +=0",
				onEnter: () => {
					ctaHome.classList.add("--open");
				},
				// markers: true,
			});
		}
	}
};

// GSAP : Animations au scroll horizontal (avec ScrollTrigger)
const gsapHorizontalAnimations = (horizontalScroll) => {
	// [data-animation] + .elementor-element
	const elementsToAnimated = document.querySelectorAll([
		"[data-animation]",
		".elementor-heading-title",
		".elementor-element > .elementor-element > .elementor-element:not([data-animation])",
	]);
	elementsToAnimated.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			containerAnimation: horizontalScroll,
			start: "left +=90%",
			end: "right +=0",
			onEnter: () => {
				setTimeout(() => {
					_this.classList.add("--animated");
				}, delay);
			},
			onLeaveBack: () => {
				_this.classList.remove("--animated");
			},
			// markers: true,
		});
	});

	// data-animation="counter"
	const elementsToCounter = document.querySelectorAll(
		'[data-animation="counter"]'
	);
	elementsToCounter.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			containerAnimation: horizontalScroll,
			start: "left +=90%",
			end: "right +=0",
			// markers: true,
			onEnter: () => {
				setTimeout(() => {
					gsap.from(_this, {
						duration: 1.5,
						textContent: 0,
						snap: { textContent: 1 },
						stagger: {
							each: 1.0,
							onUpdate: function () {
								this.targets()[0].innerHTML = Math.ceil(
									this.targets()[0].textContent
								)
									// conserver les "."
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
									// conserver les espaces
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, "&nbsp;");
							},
						},
					});
				}, delay);
			},
		});
	});

	// data-animation="reveal-text-reading"
	const textsToReading = document.querySelectorAll([
		'[data-animation="reveal-text-reading"]',
	]);
	textsToReading.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		gsap.fromTo(
			_this.querySelectorAll(".word"),
			{
				opacity: 0.33,
			},
			{
				delay: delay,
				stagger: 0.3,
				ease: "power1.out",
				opacity: 1,
				scrollTrigger: {
					trigger: _this,
					containerAnimation: horizontalScroll,
					start: "left +=90%",
					end: "right +=0",
					scrub: true,
					// markers: true,
				},
			}
		);
	});

	// #cta-home
	const ctaHome = document.querySelector("#cta-home");
	if (ctaHome) {
		ScrollTrigger.create({
			trigger: document.querySelector(".archive-featured-product-category"),
			containerAnimation: horizontalScroll,
			start: "left +=50%",
			end: "right +=0",
			onEnter: () => {
				ctaHome.classList.add("--open");
			},
			// markers: true,
		});
	}
};

// GSAP : Élement fixé au scroll (avec ScrollTrigger)
const gsapStickyElements = () => {
	const stickyElements = document.querySelectorAll("[data-sticky]");

	stickyElements.forEach((stickyElement) => {
		const stickyOffset = stickyElement.dataset.sticky
			? parseInt(stickyElement.dataset.sticky, 10)
			: 0; // Convertir en nombre et gérer l'absence de valeur
		const stickyWrapper = stickyElement.parentElement;

		ScrollTrigger.matchMedia({
			// desktop
			[`(min-width: 1024px)`]: () => {
				ScrollTrigger.create({
					trigger: stickyElement,
					start: `top-=${stickyOffset} top`, // Décalage appliqué ici
					end: () =>
						`+=${stickyWrapper.offsetHeight - stickyElement.offsetHeight}`,
					pin: true,
					pinSpacing: false,
					invalidateOnRefresh: true,
					// markers: true,
				});
			},
		});
	});
};

// GSAP : Détecte les sections avec des #ancre visibles (avec ScrollTrigger)
const gsapAnchorsDetection = () => {
	// Sélectionner tous les liens avec une ancre
	const anchorLinks = document.querySelectorAll('.menu__link[href^="#"]');
	anchorLinks.forEach((link) => {
		const targetId = link.getAttribute("href");

		// Vérifier que l'ID n'est pas juste un "#"
		if (targetId !== "#") {
			const targetSection = document.querySelector(targetId);

			if (targetSection) {
				// Ajouter des écouteurs ScrollTrigger aux sections
				ScrollTrigger.create({
					trigger: targetSection,
					start: "top 50%",
					end: "bottom 50%",
					onEnter: () => {
						link.setAttribute("aria-current", "true");
					},
					onLeave: () => {
						link.removeAttribute("aria-current");
					},
					onEnterBack: () => {
						link.setAttribute("aria-current", "true");
					},
					onLeaveBack: () => {
						link.removeAttribute("aria-current");
					},
					// markers: true,
				});
			} else {
				link.parentNode.classList.add("--hidden");
			}
		}
	});
};

// Actions exécutées après le preLoader d'accueil
const afterPreloader = () => {
	scrollEnabled(true);
	swiperInit();
	gsapAnchorsDetection();
	gsapStickyElements();
	gsapAnimations();
	scrollToAnchor();
};

// GSAP : Rafraîchissement du ScrollSmoother (avec ScrollSmoother)
const gsapScrollSmootherRefresh = () => {
	if (scrollSmooth) {
		// Désactiver sur Élémentor
		if (document.querySelector("body.elementor-editor-active")) {
			scrollSmooth.kill();
		} else {
			scrollSmooth.refresh();
		}
	}
};

// GSAP : Gestion du preloader d'accueil
const gsapPreloader = () => {
	// Chargement des fonctions GSAP préalables
	initScrollSmooth();
	initHorizontalScroll();
	scrollEnabled(false);
	gsapSplitText();

	// Lancement de l'animation de chargement
	const loaderTimeline = gsap.timeline();
	loaderTimeline
		.to("#page-preloader .preloader__wrapper", {
			delay: 0.3,
			duration: 0.6,
			ease: "power1.out",
			opacity: 0,
		})
		.set("#page-preloader", {
			visibility: "hidden",
		});

	// Functions succédentes
	loaderTimeline.add(afterPreloader, loaderTimeline.duration());
};

/* --------------------------------- (ERWAN) -------------------------------- */

// PRODUCT FILTER
const body = document.querySelector(".tax-product-category");
const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);

let radios = [];
let size = urlParams.get("size");
let priceFilter = urlParams.get("price");
let brand = urlParams.get("brand");
let material = urlParams.get("material");
let confort = urlParams.get("confort");

if (body) {
	if (brand) {
		radios.push(document.getElementById(brand));
	}
	if (material) {
		radios.push(document.getElementById(material));
	}
	if (confort) {
		radios.push(document.getElementById(confort));
	}
	if (size) {
		radios.push(document.getElementById(size));
	}
	if (priceFilter) {
		radios.push(document.getElementById(priceFilter));
	}
	if (radios.length >= 1) {
		radios.forEach((element) => {
			if (element) {
				element.checked = true;
			}
		});
	}

	const filters = document.querySelectorAll("input[type='radio']");
	filters.forEach((element) => {
		element.addEventListener("click", (event) => {
			if (urlParams.has(event.target.name)) {
				urlParams.delete(event.target.name);
			}
			if (urlParams.has("pages")) {
				urlParams.set("pages", 1);
			}
			window.location.href =
				window.location.pathname +
				"?" +
				urlParams.toString() +
				(urlParams.size > 0 ? "&" : "") +
				event.target.name +
				"=" +
				event.target.id;
		});
	});

	const filterPrice = document.querySelectorAll("input[type='checkbox']");
	filterPrice.forEach((element) => {
		element.addEventListener("click", (event) => {
			if (urlParams.has(event.target.name)) urlParams.delete(event.target.name);
			window.location.href =
				window.location.pathname +
				"?" +
				urlParams.toString() +
				(urlParams.size > 0 ? "&" : "") +
				event.target.name +
				"=" +
				event.target.value;
		});
	});

	const cleanFilter = document.querySelector("#clean-filter");
	if (cleanFilter) {
		cleanFilter.addEventListener("click", () => {
			window.location.href = window.location.href.split("?")[0];
		});
	}
}

// PRODUCT DETAIL
let prices;
const selectPrice = document.querySelector("#select-size");
if (selectPrice) {
	if (size) {
		Array.from(document.querySelector("#select-size").options).forEach(
			function (option_element) {
				const index = option_element.classList[0].split("-");
				let price = document.querySelector("#price-" + index[1]);
				if (size == option_element.text) {
					selectPrice.value = option_element.value;
					price.classList.remove("--hidden");
				} else {
					price.classList.add("--hidden");
				}
			}
		);
	}

	let price = document.querySelector("#price-0");
	let priceTotal = price.querySelector("#priceTotal").innerHTML;
	priceTotal = priceTotal.replace("€", "").replace(" ", "").replace(",", ".");

	if (parseInt(priceTotal) > 150 || priceTotal.length > 6) {
		jQuery.ajax({
			method: "GET",
			url: "?lwb_sofinco=" + priceTotal,
			beforeSend: function () {
				jQuery(".content__credit").addClass("--loading");
			},
			success: function (values) {
				jQuery(".content__credit").removeClass("--loading");

				const data = JSON.parse(values);

				jQuery("#credit-price").html(
					new Intl.NumberFormat("fr-FR", {
						style: "currency",
						currency: "EUR",
					}).format(data[0]["monthlyAmountWithoutInsurance"])
				);
				jQuery("#credit-taeg").html(
					"TAEG " +
					data[0]["annualPercentageRateOfCharge"] +
					"%. Montant total dû : " +
					new Intl.NumberFormat("fr-FR", {
						style: "currency",
						currency: "EUR",
					}).format(data[0]["totalAmountWithoutInsurance"])
				);
			},
		});

		document.querySelector("#legals-product").classList.remove("--hidden");

		document.querySelector("#credit-simulation").href = "https://www.creditpartner.fr/simulateur/?Q6=web_em&X1=simu_vac&s3="+parseFloat(priceTotal)+"&a9=99102660690&n2=014";
	} else {
		document.querySelector("#legals-product").classList.add("--hidden");
	}

	selectPrice.addEventListener("change", (event) => {
		prices = document.querySelectorAll("#prices > *");
		Array.prototype.forEach.call(prices, function (element) {
			element.classList.add("--hidden");
		});

		let price = document.querySelector("#price-" + event.target.selectedIndex);
		price.classList.remove("--hidden");

		let priceTotal = price.querySelector("#priceTotal").innerHTML;
		priceTotal = priceTotal.replace("€", "").replace(" ", "").replace(",", ".");

		if (parseInt(priceTotal) > 150 || priceTotal.length > 6) {
			jQuery.ajax({
				method: "GET",
				url: "?lwb_sofinco=" + priceTotal,
				beforeSend: function () {
					jQuery(".content__credit").addClass("__loading");
				},
				success: function (values) {
					jQuery(".content__credit").removeClass("__loading");
					const data = JSON.parse(values);

					jQuery("#credit-price").html(
						new Intl.NumberFormat("fr-FR", {
							style: "currency",
							currency: "EUR",
						}).format(data[0]["monthlyAmountWithoutInsurance"])
					);
					jQuery("#credit-taeg").html(
						"TAEG " +
						data[0]["annualPercentageRateOfCharge"] +
						"%. Montant total dû : " +
						new Intl.NumberFormat("fr-FR", {
							style: "currency",
							currency: "EUR",
						}).format(data[0]["totalAmountWithoutInsurance"])
					);
				},
			});

			document.querySelector("#legals-product").classList.remove("--hidden");

			document.querySelector("#credit-simulation").href = "https://www.creditpartner.fr/simulateur/?Q6=web_em&X1=simu_vac&s3="+parseFloat(priceTotal)+"&a9=99102660690&n2=014";
		} else {
			document.querySelector("#legals-product").classList.add("--hidden");
		}
	});
}

// JOBS
const jobParam = urlParams.get("offre");
if (jobParam) {
	const selectJob = document.querySelector("#jobs");
	const job = document.querySelector("[data-id='" + jobParam + "']");
	if (job) {
		selectJob.value = job.value;
	}
}

// SEARCHMAP
function addShop(data) {
	jQuery.ajax({
		url: "shops-json",
		data: {
			ajax: 1,
			ville: data,
		},
		beforeSend: function () {
			document.querySelector("#page-search-map").classList.add("--loading");
		},
		success: function (response) {
			jQuery(".loader-map").hide();
			const list = document.querySelector("#list-shop");
			list.innerHTML = "";

			const mapArea = document.querySelector("#map-city-choice");
			const mapDiv = document.querySelector(".searchmap__mapblock");

			if (mapArea) {
				mapArea.remove();
				let coordinate = [];
				let newMap = document.createElement("div");
				newMap.className = "searchmap__map";
				newMap.id = "map-city-choice";
				mapDiv.appendChild(newMap);
				// Initialisation de la map
				if (response.city) {
					coordinate = [response.city.lat, response.city.lon];
				} else {
					coordinate = [48.8566, 2.3522];
				}
				const leafletMap = L.map(newMap, {
					center: coordinate,
					zoom: 7,
					scrollWheelZoom: true,
				});
				L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
					leafletMap
				);

				if (response.shops) {
					response.shops.forEach((shop) => {
						list.innerHTML += `
							<div class="excerpt-shop">
								<div class="excerpt__wrapper">
									<div class="excerpt__header">
										<div class="excerpt__hgroup">
											<p class="excerpt__title title-h5">
												${shop.title}
											</p>
											<p class="excerpt__subtitle title-h0">
												${shop.localisation["adresse"]},
												${shop.localisation["code_postal"]},
												${shop.localisation["ville"]}
											</p>
										</div>
										<div class="excerpt__actions">
											<button class="exerpt__button button-primary" data-shop="${shop.id}">
												<span>Choisir ce magasin</span>
											</button>
										</div>
									</div>
								</div>
							</div>
							`;

						const marker = L.marker([shop.latitude, shop.longitude], {
							icon: L.divIcon({
								html: `
								<i style="transform: translate(-40%, -60%); filter: drop-shadow(13px 15px 10px rgba(0, 0, 0, 0.33))">
									<svg xmlns="http://www.w3.org/2000/svg" height="50" viewBox="0 0 39.7 48.88">
										<path fill="var(--color-secondary)" d="M19.85 0C8.89 0 0 8.89 0 19.85c0 7.44 6.7 17.12 19.85 29.03C33 37.22 39.7 27.29 39.7 19.85 39.7 8.89 30.81 0 19.85 0Zm.34 32.88c-7.73 0-14-6.27-14-14s6.27-14 14-14 14 6.27 14 14-6.27 14-14 14Z"/>
										<path fill="var(--color-light)" d="M20.19 4.88c-7.73 0-14 6.27-14 14s6.27 14 14 14 14-6.27 14-14-6.27-14-14-14Zm0 26c-6.29 0-11.45-4.84-11.96-11h23.92c-.51 6.16-5.67 11-11.96 11Z"/>
										<path fill="var(--color-primary)" d="M32.15 19.88c-.51 6.16-5.67 11-11.96 11s-11.45-4.84-11.96-11h23.92Z"/>
									</svg>
								</i>
							`,
							}),
						});

						leafletMap.addLayer(marker);

						var popup = `
							<div class="pin">
								<div class="pin__title">
									${shop.title}
								</div>
								<div class="pin__address">
									${shop.localisation["adresse"]}
									${shop.localisation["code_postal"]}
									${shop.localisation["ville"]}
								</div>
								<div class="pin__phone">
									${shop.phone}
								</div>
								<!--
								<div class="pin__link" data-shop="${shop.id}" role="button">
									<span>Choisir ce magasin</span>
								</div>
								-->
							</div>
						`;
						marker.bindPopup(popup);
					});
				}else {
					list.innerHTML = "<blockquote>Aucun résultat...</blockquote>";
				}
			}

			document.querySelectorAll("[data-shop]").forEach((element) => {
				element.addEventListener("click", function () {
					const uri = document.body.dataset.uri;
					jQuery.ajax({
						url: `${uri}/inc/session_shop.php`,
						data: {
							shop: element.dataset.shop,
						},
						success: function () {
							document
								.querySelector("#page-search-map")
								.classList.remove("--open");
							location.reload();
						},
					});
				});
			});
		},
	});
}

const searchCity = document.querySelector("#search-city");
if (searchCity) {
	searchCity.addEventListener("keyup", function (el) {
		if (el.key === "Enter" || el.keyCode === 13) {
			addShop(el.target.value);
		}
	});
}

const searchCitySubmit = document.querySelector("#search-city-submit");
if (searchCitySubmit) {
	searchCitySubmit.addEventListener("click", function () {
		addShop(searchCity.value);
	});
}

if (document.querySelector(".page__cta-offer")) {
	if (sessionStorage.getItem("cta_offer") != "true") {
		document.querySelector(".page__cta-offer").classList.add("--open");
		sessionStorage.setItem("cta_offer", "true");
	}else {
		document.querySelector(".page__cta-offer").classList.remove("--open");
	}


	document.addEventListener("mouseup", (event) => {
		if (
			document.querySelector(".page__cta-offer").classList.contains("--open") &&
			!document.querySelector(".page__cta-offer").contains(event.target) &&
			document.querySelector(".page__cta-offer") !== event.target
		) {
			console.log('test');
			document.querySelector(".page__cta-offer").classList.remove("--open");
		}
	});
}
